/**
 * REPSit.ai theme — colors aligned to the brand references (Beta-1 plan P6-13, BRAND; ⚑Q-25:
 * colors only, fonts stay Roboto until Tim picks fonts).
 *
 *   Deep Indigo  #1E2360  headlines / dark bands (app bar, beta ribbon)      → palette.brand.indigo
 *   Action Blue  #16699F  buttons / links, hover Deep Blue #11507A            → palette.primary
 *   Canvas Cream #FAF8F4  page background, Card White #FFFFFF surfaces        → palette.background
 *   Body Ink     #33322E  text, Muted #6B6963, Hairline #E6E1D5 dividers      → palette.text / divider
 *   Signal Amber #EF9F27  threshold / alert moments only (+ Amber Ink #854F0B on amber fills)
 *   Success Teal #0F6E56  positive status
 *   Light fills  Blue #E7F2FA / Amber #FAEEDA with borders #B7D9EF / #F1D49E   → palette.brand.fill
 *   Logo Indigo #2E357F and Logo Azure #1F80C1 are reserved for the logo / hero signal line.
 *   Text on indigo bands: Indigo Light #E3E5F6 · Soft #CFD3EE · Muted #A9AED8 · Legal #8B90C2.
 *
 * The sidebar stays Card White with the full-color logo until the reversed-white logo arrives
 * from Tim's media folder (logo rule: reversed logo on indigo). REPS quadrant colors and the
 * green / yellow / red bands are unchanged (P5-01). Dark mode derives from Deep Indigo.
 * Buttons: sentence case, never uppercase (BRAND button rule).
 */
import { createTheme } from '@mui/material/styles';

export const BRAND = Object.freeze({
  indigo: '#1E2360',
  indigoLight: '#E3E5F6',
  indigoSoft: '#CFD3EE',
  indigoMuted: '#A9AED8',
  indigoLegal: '#8B90C2',
  actionBlue: '#16699F',
  deepBlue: '#11507A',
  canvas: '#FAF8F4',
  card: '#FFFFFF',
  ink: '#33322E',
  muted: '#6B6963',
  hairline: '#E6E1D5',
  amber: '#EF9F27',
  amberInk: '#854F0B',
  teal: '#0F6E56',
  fillBlue: '#E7F2FA',
  fillBlueBorder: '#B7D9EF',
  fillAmber: '#FAEEDA',
  fillAmberBorder: '#F1D49E',
  logoIndigo: '#2E357F', // logo only
  logoAzure: '#1F80C1', // logo / hero signal line only
});

const getTheme = (mode = 'light') => {
  const light = mode === 'light';
  return createTheme({
    palette: {
      mode,
      primary: {
        main: BRAND.actionBlue,
        dark: BRAND.deepBlue,
        light: '#4E8FBF',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: BRAND.teal,
        contrastText: '#FFFFFF',
      },
      success: { main: BRAND.teal, light: '#3E8E7A', dark: '#0B5443', contrastText: '#FFFFFF' },
      warning: { main: BRAND.amber, light: BRAND.fillAmber, dark: BRAND.amberInk, contrastText: BRAND.ink },
      info: { main: BRAND.actionBlue, light: BRAND.fillBlue, dark: BRAND.deepBlue, contrastText: '#FFFFFF' },
      background: {
        default: light ? BRAND.canvas : '#12142E',
        paper: light ? BRAND.card : '#1B1E45',
      },
      text: {
        primary: light ? BRAND.ink : BRAND.indigoLight,
        secondary: light ? BRAND.muted : BRAND.indigoMuted,
      },
      divider: light ? BRAND.hairline : 'rgba(227,229,246,0.16)',
      brand: {
        ...BRAND,
        band: BRAND.indigo, // app bar / ribbon background in both modes
        onBand: BRAND.indigoLight,
        onBandMuted: BRAND.indigoMuted,
        fill: { blue: BRAND.fillBlue, blueBorder: BRAND.fillBlueBorder, amber: BRAND.fillAmber, amberBorder: BRAND.fillAmberBorder },
      },
      // REPS Quadrant colors (unchanged, P5-01)
      reps: {
        reliability: '#1565c0',
        efficiency: '#00897b',
        productivity: '#ef6c00',
        safety: '#c62828',
      },
      // Score severity colors (green / yellow / red bands unchanged, P5-01)
      severity: {
        healthy: '#2e7d32',
        monitor: '#f9a825',
        warning: '#c62828',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      // Headings step down on phones so page titles never take three lines (responsive pass 2026-09-21).
      h1: { fontWeight: 700, '@media (max-width:600px)': { fontSize: '2.6rem' } },
      h2: { fontWeight: 700, '@media (max-width:600px)': { fontSize: '2rem' } },
      h3: { fontWeight: 700, '@media (max-width:600px)': { fontSize: '1.6rem' } },
      h4: { fontWeight: 600, '@media (max-width:600px)': { fontSize: '1.5rem' } },
      h5: { fontWeight: 600, '@media (max-width:600px)': { fontSize: '1.2rem' } },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none' },
      score: {
        fontFamily: '"Roboto Mono", monospace',
        fontWeight: 700,
      },
    },
    shape: {
      borderRadius: 12,
    },
    transitions: {
      duration: {
        standard: 300,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            transition: 'background-color 0.3s ease, color 0.3s ease',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundColor: BRAND.indigo, color: BRAND.indigoLight, backgroundImage: 'none' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: light ? '0 2px 8px rgba(51,50,46,0.06)' : '0 2px 8px rgba(0,0,0,0.35)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
      },
      MuiChip: {
        styleOverrides: {
          colorWarning: { backgroundColor: BRAND.fillAmber, color: BRAND.amberInk, border: `1px solid ${BRAND.fillAmberBorder}` },
          colorInfo: { backgroundColor: BRAND.fillBlue, color: BRAND.deepBlue, border: `1px solid ${BRAND.fillBlueBorder}` },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardWarning: { backgroundColor: BRAND.fillAmber, color: BRAND.amberInk, border: `1px solid ${BRAND.fillAmberBorder}` },
          standardInfo: { backgroundColor: BRAND.fillBlue, color: BRAND.deepBlue, border: `1px solid ${BRAND.fillBlueBorder}` },
        },
      },
      // Phone/tablet defaults (responsive pass 2026-09-21): dialogs keep a gutter, popovers never
      // exceed the viewport, tables scroll inside their own container, tab bars scroll.
      MuiDialog: {
        styleOverrides: {
          paper: {
            '@media (max-width:600px)': { margin: 8, width: 'calc(100% - 16px)', maxWidth: 'calc(100% - 16px)', maxHeight: 'calc(100% - 16px)' },
          },
        },
      },
      MuiPopover: {
        styleOverrides: { paper: { maxWidth: 'calc(100vw - 16px)' } },
      },
      MuiTableContainer: {
        styleOverrides: { root: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' } },
      },
      MuiTabs: {
        defaultProps: { variant: 'scrollable', scrollButtons: 'auto', allowScrollButtonsMobile: true },
      },
      // Segmented controls wrap onto a second line on narrow screens instead of squeezing every label
      // into three lines; each button keeps its own border so a wrapped row still reads as buttons.
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: { flexWrap: 'wrap', gap: 6 },
          grouped: {
            margin: 0,
            border: `1px solid ${light ? BRAND.hairline : 'rgba(227,229,246,0.24)'}`,
            borderRadius: 8,
            '&:not(:first-of-type)': { marginLeft: 0, borderLeft: `1px solid ${light ? BRAND.hairline : 'rgba(227,229,246,0.24)'}`, borderTopLeftRadius: 8, borderBottomLeftRadius: 8 },
            '&:not(:last-of-type)': { borderTopRightRadius: 8, borderBottomRightRadius: 8 },
            '&.Mui-selected': { borderColor: BRAND.actionBlue },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: { root: { whiteSpace: 'nowrap', textTransform: 'none', fontWeight: 600 } },
      },
      MuiTextField: {
        styleOverrides: { root: { maxWidth: '100%' } },
      },
      MuiFormControl: {
        styleOverrides: { root: { maxWidth: '100%' } },
      },
    },
  });
};

export default getTheme;
